/**
 * ContentDocView (Issue #2212)
 *
 * Renders a static ContentDoc (About, FAQ, Privacy, Terms, SMS Help)
 * through the standard page components (PageHeader, Section, CalloutCard,
 * Divider, LastUpdated). The native app renders the same data through its
 * own screen — content is single-sourced in `frontend/src/data/content/`.
 *
 * Link segments render as react-router <Link> anchors, except
 * `webRender: 'button'` which renders a navigate() button (the Terms page
 * Privacy Policy links are pinned as buttons by TermsPage.test.tsx).
 */

import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  ContentDocBlock,
  ContentDoc,
  ContentInline,
  ContentDocSection,
} from '../types/content';
import OroTrademark from './OroTrademark';
import PageHeader from './PageHeader';
import Section from './Section';
import SectionGroup from './SectionGroup';
import CalloutCard from './CalloutCard';
import Divider from './Divider';
import LastUpdated from './LastUpdated';

const LINK_BASE_CLASS = 'underline transition-colors text-accent hover:text-accent/80';
const linkClass = (bold?: boolean) => `${bold ? 'font-bold' : 'font-medium'} ${LINK_BASE_CLASS}`;

/**
 * A `{ t: 'link' }` href that leaves the app: mailto:, tel:, or an absolute
 * http(s) URL. These render as a real `<a>` — a react-router `<Link>` treats
 * `mailto:support@oro.fit` as a relative in-app route (#2940). Keep in
 * lock-step with the native predicate in
 * `mobile/src/components/content/ContentDocView.tsx`.
 */
const EXTERNAL_HREF = /^(mailto:|tel:|https?:)/i;
const HTTP_HREF = /^https?:/i;

/** Compile-time exhaustiveness backstop — a new variant must add a case. */
function assertNever(value: never): null {
  console.error('[ContentDocView] unhandled content variant:', value);
  return null;
}

function InlineView({ inline }: { inline: ContentInline }) {
  const navigate = useNavigate();

  if (typeof inline === 'string') return <>{inline}</>;

  switch (inline.t) {
    case 'strong': {
      const toneClass =
        inline.tone === 'accent'
          ? 'text-accent'
          : inline.tone === 'inherit'
            ? undefined
            : 'text-text-primary';
      return <strong className={toneClass}>{inline.v}</strong>;
    }
    case 'em':
      return <em>{inline.v}</em>;
    case 'brand':
      return <OroTrademark />;
    case 'link': {
      if (inline.webRender === 'button') {
        return (
          <button
            type="button"
            onClick={() => navigate(inline.href)}
            className={linkClass(inline.bold)}
          >
            {inline.v}
          </button>
        );
      }
      if (EXTERNAL_HREF.test(inline.href)) {
        const httpProps = HTTP_HREF.test(inline.href)
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {};
        return (
          <a href={inline.href} className={linkClass(inline.bold)} {...httpProps}>
            {inline.v}
          </a>
        );
      }
      return (
        <Link to={inline.href} className={linkClass(inline.bold)}>
          {inline.v}
        </Link>
      );
    }
    case 'break':
      return <br />;
    case 'mono':
      return <span className="font-mono text-xs">{inline.v}</span>;
    default:
      return assertNever(inline);
  }
}

function Inlines({ inlines }: { inlines: ContentInline[] }) {
  return (
    <>
      {inlines.map((inline, i) => (
        <InlineView key={i} inline={inline} />
      ))}
    </>
  );
}

function BlockView({ block }: { block: ContentDocBlock }) {
  switch (block.type) {
    case 'p': {
      const classes = [
        block.emphasis ? 'text-text-primary font-medium' : undefined,
        block.lead ? 'text-lg' : undefined,
      ]
        .filter(Boolean)
        .join(' ');
      return (
        <p className={classes || undefined}>
          <Inlines inlines={block.inlines} />
        </p>
      );
    }
    case 'list': {
      if (block.style === 'plain') {
        return (
          <div className="pl-4 space-y-2">
            {block.items.map((item, i) => (
              <p key={i}>
                <Inlines inlines={item} />
              </p>
            ))}
          </div>
        );
      }
      const ListTag = block.style === 'numbered' ? 'ol' : 'ul';
      const listClass =
        block.style === 'numbered'
          ? 'list-decimal list-inside space-y-1 ml-4'
          : 'list-disc list-inside space-y-2 ml-4';
      return (
        <ListTag className={listClass}>
          {block.items.map((item, i) => (
            <li key={i}>
              <Inlines inlines={item} />
            </li>
          ))}
        </ListTag>
      );
    }
    case 'labeled':
      return (
        <div>
          <p className="font-medium text-text-primary mb-2">
            <Inlines inlines={block.label} />
          </p>
          <div className="space-y-3">
            {block.blocks.map((child, i) => (
              <BlockView key={i} block={child} />
            ))}
          </div>
        </div>
      );
    case 'box': {
      const boxClass = {
        accent: 'p-3 bg-accent/10 rounded border border-accent/20',
        surface: 'mt-4 p-3 bg-oro-surface rounded border border-oro-border',
        'surface-alt': 'p-4 bg-oro-surface-alt rounded-lg border border-oro-border',
      }[block.tone];
      return (
        <div className={boxClass}>
          {block.title && <p className="font-medium text-text-primary mb-2">{block.title}</p>}
          <div className="space-y-3">
            {block.blocks.map((child, i) => (
              <BlockView key={i} block={child} />
            ))}
          </div>
        </div>
      );
    }
    default:
      return assertNever(block);
  }
}

function SectionView({ section, doc }: { section: ContentDocSection; doc: ContentDoc }) {
  return (
    <Section
      title={section.title ? <Inlines inlines={section.title} /> : undefined}
      headingLevel={section.headingLevel}
      showDivider={section.showDivider ?? doc.sectionDividers ?? false}
      className={section.align === 'center' ? 'text-center' : undefined}
    >
      {section.blocks.map((block, i) => (
        <BlockView key={i} block={block} />
      ))}
    </Section>
  );
}

interface ContentDocViewProps {
  doc: ContentDoc;
  /** Rendered after the sections (e.g. the About page's login CTA). */
  children?: ReactNode;
}

export default function ContentDocView({ doc, children }: ContentDocViewProps) {
  return (
    <>
      <PageHeader title={<Inlines inlines={doc.title} />} subtitle={doc.subtitle} />

      {doc.callout && (
        <CalloutCard title={doc.callout.title}>
          {doc.callout.blocks.map((block, i) => (
            <BlockView key={i} block={block} />
          ))}
        </CalloutCard>
      )}

      {doc.lastUpdated && doc.lastUpdatedPosition !== 'bottom' && (
        <LastUpdated date={doc.lastUpdated} />
      )}

      {doc.groups.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 && !doc.sectionDividers && <Divider />}
          {group.length > 1 ? (
            <SectionGroup>
              {group.map((section, i) => (
                <SectionView key={i} section={section} doc={doc} />
              ))}
            </SectionGroup>
          ) : (
            group.map((section, i) => <SectionView key={i} section={section} doc={doc} />)
          )}
        </Fragment>
      ))}

      {doc.lastUpdated && doc.lastUpdatedPosition === 'bottom' && (
        <LastUpdated date={doc.lastUpdated} />
      )}

      {children}
    </>
  );
}
